import { Injectable } from '@angular/core';
import { ApiEntityService } from '@core/api/api-entity.service';
import { ApiService } from '@core/api/api.service';
import { Automapper } from '@core/api/mapper';
import { DataroomResourceBaseUrlBuilder } from '@core/dataroom/dataroom-resource-base-url-builder.service';
import { DataroomNode } from '@core/models/dataroom-node.model';
import { removeDuplicates } from '@core/utils';
import { HttpGetConfig } from '@datorama/akita-ng-entity-service';
import { Observable, BehaviorSubject } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { DataroomState, DataroomStore } from './dataroom.store';
import { EnvConfig } from '@core/config';

@Injectable({ providedIn: 'root' })
export class DataroomService extends ApiEntityService<DataroomState> {
  public uploadVectorStoreSubject = new BehaviorSubject<boolean>(false);
  public uploadVectorStore$ = this.uploadVectorStoreSubject.asObservable();

  public documentCategorizationSubject = new BehaviorSubject<boolean>(false);
  public documentCategorizationStore$ = this.documentCategorizationSubject.asObservable();

  constructor(
    protected store: DataroomStore,
    protected urlBuilder: DataroomResourceBaseUrlBuilder,
    private apiService: ApiService,
    private env: EnvConfig
  ) {
    super(store, urlBuilder, Automapper.create(DataroomNode));
    this.overrideResourceName('data-room');
  }

  private get aiEngineApiUrl(): string {
    return this.env.aiEngineApiUrl || '';
  }

  private get categorizationApiUrl(): string {
    if (!this.aiEngineApiUrl) {
      return '';
    }
    return `${this.aiEngineApiUrl}/trigger-doc-categorization-scoring-v2`;
  }

  private get apiUrl(): string {
    if (!this.aiEngineApiUrl) {
      return '';
    }
    return `${this.aiEngineApiUrl}/`;
  }

  public setLoading(loading: boolean): void {
    this.store.setLoading(loading);
  }

  public getNode(id: string): Observable<DataroomNode> {
    return this.useResourceSuffix('nodes')
      .get<DataroomNode>(id, { skipWrite: true })
      .pipe(
        take(1),
        tap(() => {
          this.setLoading(false);
        }),
      );
  }

  public getNodes(parent: string): Observable<DataroomNode[]> {
    // Normalize 'data-room' (root folder ID) to empty string to match mainParentId
    // This ensures consistent API calls and prevents duplicate 'data-room' in URL path
    const normalizedParent = parent === 'data-room' ? '' : parent;

    const httpConfig: HttpGetConfig = {
      params: normalizedParent
        ? {
          parent: normalizedParent,
        }
        : null,
      skipWrite: true,
    };
    return this.useResourceSuffix('nodes')
      .get<DataroomNode[]>({}, httpConfig)
      .pipe(
        take(1),
        tap((nodes) => {
          this.setLoading(false);
          this.updateAvailableNodes(nodes);
        }),
      );
  }

  public updateAvailableNodes(nodes: DataroomNode[]): void {
    this.store.update((state) => {
      const uniqueNodes = [...(state.nodes || []), ...nodes];
      return {
        ...state,
        nodes: removeDuplicates(uniqueNodes, (x, y) => x.id === y.id),
      };
    });
  }

  public resetAvailableNodes(): void {
    this.store.update((state) => {
      return {
        ...state,
        nodes: null,
      };
    });
  }

  public triggerCategorization(payload: any): Observable<any> {
    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(this.categorizationApiUrl, payload);
  }

  public getCategoryNamesForMissingDoc(partnerUid: string, productUid: string): Observable<any> {
    const url = `${this.aiEngineApiUrl}/get_category_names_for_missing_doc`;

    const payload = {
      partner_uid: partnerUid,
      product_uid: productUid
    };

    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(url, payload);
  }

  public getDocumentTaggingScores(
    productUid: string,
    partner_uid: string,
    isAnalyst: string,
    scopeType: string | null,
    scopeUid: string | null,
  ): Observable<any> {
    const url = `${this.aiEngineApiUrl}/get_document_tagging_scores`;

    const payload = {
      product_uid: productUid,
      partner_uid: partner_uid,
      scope_type: scopeType,
      scope_uid: scopeUid,
      is_analyst: isAnalyst
    };

    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(url, payload);
  }

  public validateDdTemplateScore(payload: {
    product_uid: string;
    partner_uid: string;
    scope_type: string;
    scope_uid: string;
  }): Observable<any> {
    const url = `${this.aiEngineApiUrl}/validate_dd_template_score`;

    return this.apiService.post(url, payload);
  }

  public getActiveCategories(partnerUid: string): Observable<any> {
    const url = `${this.aiEngineApiUrl}/category/get-active`;

    const payload = {
      partner_uid: partnerUid
    };

    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(url, payload);
  }

  public updateDocumentCategoryMappingScore(payload: { node_id: string }): Observable<any> {
    const url = `${this.aiEngineApiUrl}/update_document_category_mapping_score_is_active`;

    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(url, payload);
  }

  // check vectordb id
  public checkVectorDb(payload: any): Observable<any> {
    // If AI engine is not configured, skip calling non-existent endpoints.
    if (!this.apiUrl) {
      return new Observable((subscriber) => {
        subscriber.complete();
      });
    }

    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(this.apiUrl + "check-vectordb", payload);
  }

  public uploadVectorStore(payload: any): Observable<any> {
    // If AI engine is not configured, skip calling non-existent endpoints.
    if (!this.apiUrl) {
      return new Observable((subscriber) => {
        subscriber.complete();
      });
    }

    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(this.apiUrl + "upsert-background", payload);
  }

  public uploadVectorStoreV2(payload: any): Observable<any> {
    // If AI engine is not configured, skip calling non-existent endpoints.
    if (!this.apiUrl) {
      return new Observable((subscriber) => {
        subscriber.complete();
      });
    }

    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(this.apiUrl + "upsert-background-v2", payload);
  }

  public documentCategorization(payload: any): Observable<any> {
    // If AI engine is not configured, skip calling non-existent endpoints.
    if (!this.apiUrl) {
      return new Observable((subscriber) => {
        subscriber.complete();
      });
    }

    // Headers are automatically added by AiEngineApiInterceptor
    // return this.apiService.post(this.apiUrl + "doc-category-background", payload);
    return this.apiService.post(`${this.aiEngineApiUrl}/doc-cat-scoring-background`, payload);
  }

  public deleteVectorStore(payload: any): Observable<any> {
    // If AI engine is not configured, skip calling non-existent endpoints.
    if (!this.apiUrl) {
      return new Observable((subscriber) => {
        subscriber.complete();
      });
    }

    return this.apiService.delete(this.apiUrl + "vector-stores/files/delete", { body: payload });
  }

  public insertTemplateProductMapping(payload : any): Observable<any> {
    const url = `${this.aiEngineApiUrl}/insert_template_product_mapping`;
    // Headers are automatically added by AiEngineApiInterceptor
    return this.apiService.post(url, payload);
  }

  public createTemplateProductMapping(payload: any): Observable<any> {
    const url = `${this.aiEngineApiUrl}/create_template_product_mapping`;
    return this.apiService.post(url, payload);
  }

}
